import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AddressInput from '@/components/forms/AddressInput';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentPassenger } from '@/data/passenger.slice';
import { useClientMemberContact } from '@/hooks/useClientMemberContact';
import { usePortalLabel } from '@/hooks/usePortalLabel';
import reservationUtils from '@/services/reservationUtils';
import { TripLegType } from '@/data/models/reservation.model';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import { isLowConfidenceError } from '../machines/tripLegConfidence';

export function TripArrayFieldAddressInput({
  index,
  tripField,
  label,
  required,
}: {
  tripField: 'pickupAddress' | 'dropoffAddress';
  index: number;
  label: string;
  required?: boolean;
}) {
  const { t } = useTranslation('common');
  const { watch, setValue, setError, clearErrors, formState } =
    useFormContext<ReservationFormInput>();
  const isClientPortal = usePortalLabel() === 'client-portal';
  const { mainSnapshot, fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const name = `trips.${index}.${tripField}` as const;
  const place = useWatch({ name });

  useEffect(() => {
    actor.send({
      type: 'CHANGE',
      index,
      value: place ?? '',
    });
  }, [actor, index, place]);

  const passenger = useAppSelector(selectCurrentPassenger);
  const passengerAddresses = useMemo(
    () => passenger?.passengerAddresses ?? [],
    [passenger],
  );
  // {{clientName}}/{{clientNumber}} interpolation for LOW_CONFIDENCE_ADDRESS.
  // The contact is plan-scoped, so the passenger's service plan rides along.
  const clientMemberContact = useClientMemberContact(
    passenger?.clientBC,
    passenger?.servicePlans?.[0]?.servicePlanUUID,
  );

  const structFieldError =
    formState.errors.trips?.[index]?.[tripField]?.message;

  const addressValidationEnabled =
    mainSnapshot.context.featureFlags?.addressValidationEnabled ?? false;

  const tripType = useWatch({ name: 'tripType' });

  // Client portal surfaces low address-confidence (/validate's
  // LOW_CONFIDENCE_ADDRESS) as a non-blocking yellow warning under the field,
  // instead of the red blocking error other portals get (suppressed in the
  // CreateReservationDialog applier). Map the low-confidence /validate errors to
  // their form fields with the same resolver the red-error path uses (round-trip
  // return-leg reversal aware) and show the one that lands on THIS field.
  const confidenceWarningMessage = useMemo(() => {
    if (!isClientPortal || !addressValidationEnabled) return undefined;

    const lowConfidence = (
      mainSnapshot.context.lastValidationResult?.errors ?? []
    ).filter(isLowConfidenceError);
    if (lowConfidence.length === 0) return undefined;

    const mapped = reservationUtils.getReservationFormFieldErrors(
      t,
      tripType === TripLegType.RoundTrip,
      { tripLegUpdates: [], errors: lowConfidence, warnings: [], log: [] },
      clientMemberContact,
    );
    // getReservationFormFieldErrors emits bracket paths (`trips[0].x`); normalize
    // to the dot paths used as `name` (`trips.0.x`).
    const hit = mapped.find(
      (m) => m.field.replace(/\[(\d+)\]/g, '.$1') === name,
    );
    return hit?.message || undefined;
  }, [
    isClientPortal,
    addressValidationEnabled,
    clientMemberContact,
    mainSnapshot,
    name,
    t,
    tripType,
  ]);

  return (
    <AddressInput
      name={name}
      label={label}
      required={required}
      errorMessage={structFieldError ? String(structFieldError) : ''}
      warningMessage={confidenceWarningMessage}
      watch={watch}
      setValue={setValue}
      errorHandlers={{ setError, clearErrors }}
      passengerAddresses={passengerAddresses}
    />
  );
}
